import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import type { AuthUser } from '../api/types';
import { type AppEnv, runningEnv } from '../env/appEnv';
import { resetSessionExpiredEventDeduplication } from './sessionEvents';

// R6-02-c: Prod und Beta teilen sich denselben WebView-Ursprung und damit ALLE
// Speicher. Ein Beta-Login wuerde sonst den Prod-Token unter DEMSELBEN Schluessel
// ueberschreiben — nach der Rueckkehr antwortet app.lorath.eu mit
// session_expired und der Nutzer fliegt aus der Produktion. Die Produktions-
// Schluessel bleiben deshalb BITGLEICH; nur die Beta bekommt eigene.
export const AUTH_TOKEN_KEY_PRODUCTION = 'lorath_auth_token_react_v1';
export const AUTH_TOKEN_KEY_BETA = 'lorath_auth_token_react_beta_v1';
export const AUTH_USER_SNAPSHOT_KEY_PRODUCTION = 'lorath_auth_user_snapshot_v1';
export const AUTH_USER_SNAPSHOT_KEY_BETA = 'lorath_auth_user_snapshot_beta_v1';

export function authTokenKeyFor(env: AppEnv): string {
  return env === 'beta' ? AUTH_TOKEN_KEY_BETA : AUTH_TOKEN_KEY_PRODUCTION;
}

export function authUserSnapshotKeyFor(env: AppEnv): string {
  return env === 'beta' ? AUTH_USER_SNAPSHOT_KEY_BETA : AUTH_USER_SNAPSHOT_KEY_PRODUCTION;
}

// Bewusst pro Zugriff ausgewertet (nicht als Modul-Konstante): so bleibt die
// Wahl in Tests umschaltbar und haengt nicht an der Ladereihenfolge.
function authTokenKey(): string {
  return authTokenKeyFor(runningEnv());
}

function authUserSnapshotKey(): string {
  return authUserSnapshotKeyFor(runningEnv());
}

// W1-B: Ersatz-Geraetekennung fuer die Gast-Tuer im Web. Nativ liefert
// getDeviceInfo() eine echte Geraete-ID; im Browser gibt es keine, also legen
// wir hier EINEN zufaelligen Schluessel ab. Er ist ausschliesslich der
// Limit-Schluessel des Servers (Deckel je Geraet) — KEIN Wiederherstellungs-
// schluessel: ohne Konto ist ein geleerter Speicher weiterhin Datenverlust.
const GUEST_DEVICE_KEY = 'lorath_guest_device_key_v1';

type PreferencesApi = typeof Preferences;

// Das Plugin-Objekt NIE direkt aus einer async-Funktion zurueckgeben:
// Capacitors Plugin-Proxy beantwortet JEDEN Property-Zugriff als Plugin-
// Methode — auch `.then`. Eine Promise, die das Plugin aufloest, haelt es
// dadurch fuer ein Thenable und ruft `Preferences.then()` auf → Crash
// '"Preferences.then()" is not implemented on android' (APK-Haenger
// 2026-06-10). Darum in ein neutrales Objekt wrappen.
interface PreferencesHandle { plugin: PreferencesApi }

let preferencesPromise: Promise<PreferencesHandle | null> | null = null;
let cachedNativeToken: string | null | undefined;
let nativeTokenRevision = 0;

function getLegacyWebStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

function clearLegacyWebToken(): void {
  try {
    getLegacyWebStorage()?.removeItem(authTokenKey());
  } catch {
    // Legacy cleanup must never block auth bootstrap.
  }
}

function readLocalStorageItem(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorageItem(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage quotas or privacy modes must not break auth.
  }
}

function removeLocalStorageItem(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Auth cleanup is best-effort in restricted storage modes.
  }
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true';
  return false;
}

function normalizeAuthUserSnapshot(value: unknown): AuthUser | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const userId = readNumber(record.userId ?? record.id);
  const username = readString(record.username).trim();
  if (userId === null || !username) return null;
  return {
    userId,
    username,
    display_name: readString(record.display_name) || username,
    role: readString(record.role) || 'user',
    is_admin: readBoolean(record.is_admin),
    membership_tier: (readString(record.membership_tier) || 'free') as AuthUser['membership_tier'],
    beta_access: readBoolean(record.beta_access),
    email: readString(record.email),
    // A3: Der Offline-Kaltstart behält Gaststatus, Geburtsdatums-Gate und
    // Einstiegssicht aus /me; fehlende Neulingsdaten bleiben unbekannt.
    is_guest: readBoolean(record.is_guest),
    profile_incomplete: readBoolean(record.profile_incomplete),
    registered_at: readString(record.registered_at) || null,
    own_turns: readNumber(record.own_turns) ?? undefined,
    guest_oauth_allowed: record.guest_oauth_allowed === true,
    guest_token_expires_at: typeof record.guest_token_expires_at === 'number'
      ? record.guest_token_expires_at : readString(record.guest_token_expires_at) || null,
    newcomer_mode: record.newcomer_mode === undefined ? undefined : readBoolean(record.newcomer_mode),
    coin_balance: readNumber(record.coin_balance) ?? undefined,
    free_budget_messages_remaining: readNumber(record.free_budget_messages_remaining) ?? undefined,
    free_budget_messages_expires_at: readString(record.free_budget_messages_expires_at) || null,
    free_normal_messages_remaining: readNumber(record.free_normal_messages_remaining) ?? undefined,
    free_normal_messages_expires_at: readString(record.free_normal_messages_expires_at) || null,
    // W2-B/X-09 (Review m8): die Fakten der fokussierten Leiste gehoeren in den
    // Snapshot — sonst zeigt ein Offline-Kaltstart einem Neukonto die klassische
    // Leiste (Welt/Tafelrunde) und online wieder die fokussierte. Dieselbe
    // Normalisierung wie layout/useNavFocus: fehlende Werte bleiben weg.
    account_created_at: readString(record.account_created_at) || null,
    own_message_count: readNumber(record.own_message_count) ?? undefined,
    leads_table_round: readBoolean(record.leads_table_round),
    nav_focus_since: readString(record.nav_focus_since) || null,
  };
}

export function readCachedAuthUserSnapshot(): AuthUser | null {
  const raw = readLocalStorageItem(authUserSnapshotKey());
  if (!raw) return null;
  try {
    return normalizeAuthUserSnapshot(JSON.parse(raw));
  } catch {
    removeLocalStorageItem(authUserSnapshotKey());
    return null;
  }
}

export function writeCachedAuthUserSnapshot(user: AuthUser): void {
  const snapshot = normalizeAuthUserSnapshot(user);
  if (!snapshot) return;
  writeLocalStorageItem(authUserSnapshotKey(), JSON.stringify(snapshot));
}

export function clearCachedAuthUserSnapshot(): void {
  removeLocalStorageItem(authUserSnapshotKey());
}

function getNativePreferences(): Promise<PreferencesHandle | null> {
  if (preferencesPromise) return preferencesPromise;

  const pending = Promise.resolve().then<PreferencesHandle | null>(() => {
    if (!Capacitor.isNativePlatform()) return null;
    // Warum: Preferences UND Capacitor sind absichtlich statisch importiert.
    // Beim OTA-Kaltstart darf kein kurzzeitig fehlgeschlagener Lazy-Chunk fuer
    // die gesamte Sitzung als "kein Token" festgeschrieben werden.
    return { plugin: Preferences };
  });

  preferencesPromise = pending;
  void pending.catch(() => {
    // Auch die Plattform-Erkennung kann beim frischen Native-Boot einmalig
    // scheitern. Eine abgelehnte Initialisierung muss beim naechsten Zugriff
    // erneut versucht werden koennen.
    if (preferencesPromise === pending) preferencesPromise = null;
  });
  return pending;
}

export interface StoredAuthTokenState {
  isNative: boolean;
  token: string | null;
}

export async function getStoredAuthTokenState(): Promise<StoredAuthTokenState> {
  const preferences = await getNativePreferences();
  if (!preferences) {
    clearLegacyWebToken();
    return { isNative: false, token: null };
  }

  if (cachedNativeToken !== undefined) {
    return { isNative: true, token: cachedNativeToken };
  }

  const revisionBeforeRead = nativeTokenRevision;
  const { value } = await preferences.plugin.get({ key: authTokenKey() });
  // Ein langsames Boot-get darf keinen Token ueberschreiben, den ein parallel
  // abgeschlossener Login bereits per setStoredAuthToken gesetzt hat.
  if (revisionBeforeRead === nativeTokenRevision && cachedNativeToken === undefined) {
    cachedNativeToken = value ?? null;
  }
  return { isNative: true, token: cachedNativeToken === undefined ? value ?? null : cachedNativeToken };
}

export async function getStoredAuthToken(): Promise<string | null> {
  return (await getStoredAuthTokenState()).token;
}

export async function setStoredAuthToken(token: string): Promise<void> {
  const preferences = await getNativePreferences();
  if (!preferences) {
    clearLegacyWebToken();
    nativeTokenRevision += 1;
    cachedNativeToken = null;
    return;
  }

  const revision = ++nativeTokenRevision;
  cachedNativeToken = token;
  try {
    await preferences.plugin.set({ key: authTokenKey(), value: token });
    resetSessionExpiredEventDeduplication();
  } catch (error) {
    // Der Cache darf einen fehlgeschlagenen Persist-Vorgang nicht als Erfolg
    // ausgeben. Neuere parallele Mutationen bleiben dabei unangetastet.
    if (nativeTokenRevision === revision) cachedNativeToken = undefined;
    throw error;
  }
}

export async function clearStoredAuthToken(): Promise<void> {
  const preferences = await getNativePreferences();
  nativeTokenRevision += 1;
  cachedNativeToken = null;
  if (!preferences) {
    clearLegacyWebToken();
    return;
  }

  await preferences.plugin.remove({ key: authTokenKey() });
}

function randomGuestDeviceKey(): string {
  const cryptoApi = typeof globalThis !== 'undefined'
    ? (globalThis.crypto as Crypto | undefined)
    : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Letzte Reserve (alte WebViews ohne crypto): reicht als Limit-Schluessel.
  return `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Gibt den (bei Bedarf neu erzeugten) Web-Gast-Geraeteschluessel zurueck.
 * Ist der Speicher gesperrt (Privatmodus, blockierte Cookies), liefert die
 * Funktion trotzdem einen Wert — dann eben einen fluechtigen.
 */
export function getOrCreateGuestDeviceKey(): string {
  const existing = readLocalStorageItem(GUEST_DEVICE_KEY)?.trim();
  if (existing) return existing;
  const created = randomGuestDeviceKey();
  writeLocalStorageItem(GUEST_DEVICE_KEY, created);
  return created;
}
